// Iter 187 — shared format/icon registry consumed by ContentBucket + Calendar.
// Renders a tiny colored icon based on either an explicit `icon_key` (activity
// master) OR a bucket item's `format` field.
import React from "react";
import {
  FaInstagram, FaLinkedin, FaXTwitter, FaYoutube, FaFacebook,
  FaTiktok, FaWhatsapp, FaMeta, FaGoogle,
} from "react-icons/fa6";
import {
  Mail, FileText, Search, Image as ImageIcon, Video, Mic,
  Presentation, Megaphone, Sparkles, Briefcase, Newspaper,
} from "lucide-react";

// Semantic key -> {Component, defaultColor}
const ICON_MAP = {
  instagram:    { C: FaInstagram, color: "#e1306c" },
  linkedin:     { C: FaLinkedin,  color: "#0a66c2" },
  x:            { C: FaXTwitter,  color: "#0f172a" },
  youtube:      { C: FaYoutube,   color: "#ff0000" },
  facebook:     { C: FaFacebook,  color: "#1877f2" },
  meta:         { C: FaMeta,      color: "#1877f2" },
  google:       { C: FaGoogle,    color: "#4285f4" },
  tiktok:       { C: FaTiktok,    color: "#000000" },
  whatsapp:     { C: FaWhatsapp,  color: "#25d366" },
  mail:         { C: Mail,        color: "#0ea5e9" },
  blog:         { C: Newspaper,   color: "#f59e0b" },
  search:       { C: Search,      color: "#0891b2" },
  image:        { C: ImageIcon,   color: "#a855f7" },
  video:        { C: Video,       color: "#ef4444" },
  podcast:      { C: Mic,         color: "#8b5cf6" },
  presentation: { C: Presentation,color: "#f97316" },
  megaphone:    { C: Megaphone,   color: "#facc15" },
  sparkles:     { C: Sparkles,    color: "#c026d3" },
  file:         { C: FileText,    color: "#6b7280" },
  briefcase:    { C: Briefcase,   color: "#334155" },
};

// Content Bucket `format` -> semantic icon key
export const FORMAT_ICON_KEY = {
  blog_post: "blog",
  linkedin_post: "linkedin",
  x_post: "x",
  instagram_caption: "instagram",
  instagram_carousel: "instagram",
  instagram_reel_script: "instagram",
  ad_copy_meta: "meta",
  ad_copy_google: "google",
  seo_keywords: "search",
  seo_meta: "search",
  email_newsletter: "mail",
  whatsapp_broadcast: "whatsapp",
  poster_image: "image",
  short_video_script: "video",
  youtube_script: "youtube",
};

export function iconKeyForFormat(format) {
  return FORMAT_ICON_KEY[format] || "file";
}

export function colorForFormat(format) {
  const k = iconKeyForFormat(format);
  return ICON_MAP[k]?.color || "#6b7280";
}

// Renders a tiny colored icon. Accepts either `iconKey` (activity master) or
// `format` (bucket). Optionally override the color explicitly (activity type
// custom color takes precedence over the icon default).
export function TypeIcon({ iconKey, format, color, size = 12, className = "",
                                  title, ...rest }) {
  const k = iconKey || (format ? iconKeyForFormat(format) : null);
  if (!k || !ICON_MAP[k]) return null;
  const { C, color: dflt } = ICON_MAP[k];
  const c = color || dflt;
  return (
    <C size={size} color={c} className={className}
        title={title || k} aria-label={title || k} {...rest} />
  );
}

// Small chip: icon + optional label (e.g. deliverable chip)
export function TypeChip({ iconKey, format, color, label, size = 11,
                                  className = "" }) {
  const c = color || (iconKey ? ICON_MAP[iconKey]?.color : null)
                       || colorForFormat(format);
  return (
    <span className={`inline-flex items-center gap-1 border px-1 py-0.5 text-[10px] font-mono uppercase ${className}`}
             style={{ borderColor: c + "66", background: c + "10", color: c }}>
      <TypeIcon iconKey={iconKey} format={format} color={c} size={size} />
      {label && <span className="truncate">{label}</span>}
    </span>
  );
}

export default TypeIcon;
